/**
 * Categories Service (story 22770)
 *
 * Client for the Survey category tree (CategoriesController, Survey.API).
 * Reads are anonymous; create/update/delete are Admin-role gated server-side.
 *
 * Aligned to Survey.Models.CategoriesModel (write shape) and
 * Survey.Models.CategoryTreeNodeModel (the slim GetTree projection).
 * The PUT replaces the whole row, so an edit must start from the full
 * CategoriesModel (getCategory) and only change the fields the admin touched.
 */

import { authenticatedFetch } from '../api/authenticatedFetch';

const SURVEY_BASE: string = import.meta.env.VITE_SURVEY_API_URL || '';
const BASE = `${SURVEY_BASE}/api/Survey/Categories`;

/** Depth requested from GetTree; the server clamps to its own MaxAcceptedDepth. */
export const TREE_MAX_DEPTH = 3;

/** Mirrors CategoryTreeNodeModel. `inverseParent` is the child list (EF wire name). */
export interface CategoryTreeNode {
  categoryId: number;
  title?: string | null;
  shortDescription?: string | null;
  smallImageUrl?: string | null;
  hidden?: boolean | null;
  sortOrder?: number | null;
  inverseParent: CategoryTreeNode[];
}

/** Scalar fields of CategoriesModel; the navigation collections are never sent back. */
export interface Category {
  categoryId: number;
  title?: string | null;
  shortDescription?: string | null;
  description?: string | null;
  keywords?: string | null;
  metaTitle?: string | null;
  metaDescription?: string | null;
  metaKeywords?: string | null;
  link?: string | null;
  smallImageUrl?: string | null;
  largeImageUrl?: string | null;
  featured?: boolean | null;
  hidden?: boolean | null;
  closed?: boolean | null;
  noFollow?: boolean | null;
  displayColumns?: boolean | null;
  count?: number | null;
  countTotal?: number | null;
  level?: number | null;
  leftLeaf?: number | null;
  rightLeaf?: number | null;
  parentId?: number | null;
  childRowId?: number | null;
  impressions?: number | null;
  impressionsSearch?: number | null;
  ip?: string | null;
  importId?: number | null;
  aiPrompt?: string | null;
  goodForTags?: string | null;
  isActive: boolean;
  sortOrder?: number | null;
  fixedQuestionOptionsJson?: string | null;
}

/** Fields the admin form can set when creating or editing a category. */
export interface CategoryInput {
  title: string;
  shortDescription: string;
  hidden: boolean;
  isActive: boolean;
  sortOrder: number | null;
}

/**
 * The API answers failures with BadRequest(e.Message) (plain text) or a
 * ModelState/ProblemDetails JSON object. Surface whichever text arrives.
 */
async function readError(response: Response): Promise<Error> {
  const raw = (await response.text().catch(() => '')).trim();
  let message = raw;
  if (raw.startsWith('{') || raw.startsWith('"')) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed === 'string') {
        message = parsed;
      } else if (parsed && typeof parsed === 'object') {
        const obj = parsed as { message?: string; title?: string; errors?: Record<string, string[]> };
        const details = obj.errors ? Object.values(obj.errors).flat().join(' ') : '';
        message = obj.message ?? ([obj.title, details].filter(Boolean).join(': ') || raw);
      }
    } catch {
      // not JSON after all; keep the raw text
    }
  }
  return new Error(message || `HTTP ${response.status}: ${response.statusText}`);
}

/** GET /api/Survey/Categories/GetTree?maxDepth= - returns the root node. */
export async function getCategoryTree(maxDepth: number = TREE_MAX_DEPTH): Promise<CategoryTreeNode> {
  const response = await authenticatedFetch(`${BASE}/GetTree?maxDepth=${maxDepth}`);
  if (!response.ok) throw await readError(response);
  return response.json() as Promise<CategoryTreeNode>;
}

/** GET /api/Survey/Categories/{id}/ - full row. The API returns nothing for hidden categories. */
export async function getCategory(id: number): Promise<Category> {
  const response = await authenticatedFetch(`${BASE}/${id}/`);
  if (!response.ok) throw await readError(response);
  const category = (await response.json().catch(() => null)) as Category | null;
  if (!category) {
    throw new Error(`Category ${id} could not be loaded (hidden categories cannot be fetched by id).`);
  }
  return category;
}

/** POST /api/Survey/Categories/ */
export async function createCategory(
  input: CategoryInput,
  parent: { categoryId: number; level?: number | null } | null,
): Promise<Category> {
  const response = await authenticatedFetch(`${BASE}/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      title: input.title,
      shortDescription: input.shortDescription,
      hidden: input.hidden,
      isActive: input.isActive,
      sortOrder: input.sortOrder,
      parentId: parent ? parent.categoryId : null,
      level: parent && parent.level != null ? parent.level + 1 : null,
    }),
  });
  if (!response.ok) throw await readError(response);
  return response.json() as Promise<Category>;
}

/** PUT /api/Survey/Categories/ - `current` is the full row from getCategory; only form fields change. */
export async function updateCategory(current: Category, input: CategoryInput): Promise<Category> {
  const response = await authenticatedFetch(`${BASE}/`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...current,
      title: input.title,
      shortDescription: input.shortDescription,
      hidden: input.hidden,
      isActive: input.isActive,
      sortOrder: input.sortOrder,
    }),
  });
  if (!response.ok) throw await readError(response);
  return response.json() as Promise<Category>;
}

/** DELETE /api/Survey/Categories/{id}/ */
export async function deleteCategory(id: number): Promise<void> {
  const response = await authenticatedFetch(`${BASE}/${id}/`, { method: 'DELETE' });
  if (!response.ok) throw await readError(response);
}
